import React, { useState, useCallback, useEffect, useRef } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import FormCard from "../FormComponents/FormCard";
import FormGroup from "../FormComponents/FormGroup";
import FormGrid from "../FormComponents/FormGrid";
import AlertBanner from "../FormComponents/AlertBanner";
import ExhibitorInfoBar from "../FormComponents/ExhibitorInfoBar";
import SignatureSection from "../FormComponents/SignatureSection";
import PaymentSection from "../FormComponents/PaymentSection";
import Icon from "../Icons/Icon";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Step3 = ({
  exhibitorData,
  initialContract,
  onSave,
  onDataChange,
  disabled = false,
  errors = {},
  isAdmin = false,
}) => {
  const [formData, setFormData] = useState({
    firstBoothChecked: initialContract?.firstBoothChecked ?? false,
    subsequentBoothChecked: initialContract?.subsequentBoothChecked ?? false,
    liveSubsequent: initialContract?.liveSubsequent ?? 1,
    customBoothChecked: initialContract?.customBoothChecked ?? false,
    customSize: initialContract?.customSize ?? "",
    customUnitCost: initialContract?.customUnitCost ?? "",
    customUnits: initialContract?.customUnits ?? "",
    programReviewDate: initialContract?.programReviewDate ?? "",
    referenceNumber: initialContract?.referenceNumber ?? "",
    boothConfig: initialContract?.boothConfig ?? "",
    comments: initialContract?.comments ?? "",
    pegboardChecked: initialContract?.pegboardChecked ?? false,
    pegboardQty: initialContract?.pegboardQty ?? "",
    pegboardTotal: initialContract?.pegboardTotal ?? "",
    setupMonday: initialContract?.setupMonday ?? false,
    setupTuesday: initialContract?.setupTuesday ?? false,
    sellBooth: initialContract?.sellBooth ?? "",
    palletsChecked: initialContract?.palletsChecked ?? false,
    palletsQty: initialContract?.palletsQty ?? "",
    palletsTotal: initialContract?.palletsTotal ?? "",
    palletsUnitCost: initialContract?.palletsUnitCost ?? 375,
    cratesChecked: initialContract?.cratesChecked ?? false,
    cratesQty: initialContract?.cratesQty ?? "",
    cratesTotal: initialContract?.cratesTotal ?? "",
    cratesUnitCost: initialContract?.cratesUnitCost ?? 425,
    virtualMeeting30min: initialContract?.virtualMeeting30min ?? false,
    virtualMeeting30minWeek1: initialContract?.virtualMeeting30minWeek1 ?? "",
    virtualMeeting30minWeek2: initialContract?.virtualMeeting30minWeek2 ?? "",
    virtualMeeting30minUnitCost: initialContract?.virtualMeeting30minUnitCost ?? 1200,
    virtualMeeting20min: initialContract?.virtualMeeting20min ?? false,
    virtualMeeting20minWeek1: initialContract?.virtualMeeting20minWeek1 ?? "",
    virtualMeeting20minWeek2: initialContract?.virtualMeeting20minWeek2 ?? "",
    virtualMeeting20minUnitCost: initialContract?.virtualMeeting20minUnitCost ?? 850,
    merchantNotes: initialContract?.merchantNotes ?? "",
    newProductsParticipate: initialContract?.newProductsParticipate || "",
    newProductsChecked: initialContract?.newProductsChecked ?? false,
    newProductsCost: initialContract?.newProductsCost ?? "",
    newProductsUnitCost: initialContract?.newProductsUnitCost ?? 175,
    powerAlleyParticipate: initialContract?.powerAlleyParticipate || "",
    powerAlleyChecked: initialContract?.powerAlleyChecked ?? false,
    powerAlleyQty: initialContract?.powerAlleyQty ?? "",
    powerAlleyTotal: initialContract?.powerAlleyTotal ?? "",
    powerAlleyUnitCost: initialContract?.powerAlleyUnitCost ?? 375,
    doorbustersParticipate: initialContract?.doorbustersParticipate || "",
    doorbustersChecked: initialContract?.doorbustersChecked ?? false,
    doorbustersQty: initialContract?.doorbustersQty ?? "",
    doorbustersTotal: initialContract?.doorbustersTotal ?? "",
    doorbustersUnitCost: initialContract?.doorbustersUnitCost ?? 300,
    signatureName: initialContract?.signatureName ?? "",
    signatureDate: initialContract?.signatureDate ?? "",
    signatureAcknowledge: initialContract?.signatureAcknowledge ?? false,
    accountingContact: initialContract?.accountingContact ?? "",
    billingAddress: initialContract?.billingAddress ?? "",
    billingCityStateZip: initialContract?.billingCityStateZip ?? "",
    billingPhone: initialContract?.billingPhone ?? "",
    email: initialContract?.email ?? "",
    creditCard: initialContract?.creditCard ?? "",
    nameOnCard: initialContract?.nameOnCard ?? "",
    expirationDate: initialContract?.expirationDate ?? "",
    paymentMethod: initialContract?.paymentMethod ?? "",
    invoiceAddressText: initialContract?.invoiceAddressText ?? "",
  });

  const [emailFormatError, setEmailFormatError] = useState("");
  const [customSizeFormatError, setCustomSizeFormatError] = useState("");

  const formDataRef = useRef(formData);
  const autoSaveTimerRef = useRef(null);
  const hasChangedRef = useRef(false);

  // Keep ref in sync
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Notify parent of data changes
  useEffect(() => {
    if (onDataChange) onDataChange(formData);
  }, [formData, onDataChange]);

  // Auto-save with debounce
  useEffect(() => {
    if (!hasChangedRef.current || disabled) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      if (onSave) onSave(formDataRef.current);
    }, 3000);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [formData, onSave, disabled]);

  // Save on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      if (hasChangedRef.current && onSave) {
        onSave(formDataRef.current);
      }
    };
  }, [onSave]);

  const updateField = useCallback((field, value) => {
    hasChangedRef.current = true;
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const calculateBoothTotal = () => {
    const firstBoothCost = formData.firstBoothChecked ? 7975 : 0;
    const subsequentCost = formData.subsequentBoothChecked ? 7500 * formData.liveSubsequent : 0;
    const customCost =
      formData.customBoothChecked && formData.customUnitCost && formData.customUnits
        ? parseFloat(formData.customUnitCost) * parseFloat(formData.customUnits)
        : 0;
    return 75 + firstBoothCost + subsequentCost + customCost;
  };

  const calculateTotal = () => {
    const pegboardCost = formData.pegboardChecked && formData.pegboardTotal ? parseFloat(formData.pegboardTotal) : 0;
    const palletsCost = formData.palletsChecked && formData.palletsTotal ? parseFloat(formData.palletsTotal) : 0;
    const cratesCost = formData.cratesChecked && formData.cratesTotal ? parseFloat(formData.cratesTotal) : 0;
    const virtualMeetingCost = formData.virtualMeeting30min ? (parseFloat(formData.virtualMeeting30minUnitCost) || 0) : 0;
    const virtualMeeting20Cost = formData.virtualMeeting20min ? (parseFloat(formData.virtualMeeting20minUnitCost) || 0) : 0;
    const newProductsCost = formData.newProductsParticipate === "yes" ? (parseFloat(formData.newProductsUnitCost) || 0) : 0;
    const powerAlleyCost = formData.powerAlleyParticipate === "yes" && formData.powerAlleyTotal ? parseFloat(formData.powerAlleyTotal) : 0;
    const doorbustersCost = formData.doorbustersParticipate === "yes" && formData.doorbustersTotal ? parseFloat(formData.doorbustersTotal) : 0;
    return calculateBoothTotal() + pegboardCost + palletsCost + cratesCost + virtualMeetingCost + virtualMeeting20Cost + newProductsCost + powerAlleyCost + doorbustersCost;
  };

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <fieldset disabled={disabled} style={{ border: "none", padding: 0, margin: 0 }}>
      <div className="form-page">
        <div className="form-page-header">
          <h2 className="form-page-title">Booth Contract/Invoice</h2>
          <p className="form-page-subtitle">ARETT SALES OPEN HOUSE 2026</p>
        </div>

        {hasErrors && (
          <AlertBanner type="warning">
            <strong>Please fix the following errors:</strong>{" "}
            {Object.values(errors).join(", ")}
          </AlertBanner>
        )}

        <ExhibitorInfoBar exhibitorData={exhibitorData} />

        <FormCard>
          <FormGrid cols={2}>
            <FormGroup label="Program Review Date" error={errors.programReviewDate}>
              <DatePicker
                style={{ width: "100%" }}
                className={`form-datepicker${errors.programReviewDate ? " input-error" : ""}`}
                value={formData.programReviewDate ? dayjs(formData.programReviewDate) : null}
                onChange={(date) => updateField("programReviewDate", date ? date.format("YYYY-MM-DD") : "")}
                format="MM/DD/YYYY"
              />
            </FormGroup>
            <FormGroup label="Reference Number">
              <input
                className="form-input"
                value={formData.referenceNumber}
                readOnly
              />
            </FormGroup>
          </FormGrid>
          <div className="section-divider" />
          <div className="flex-row" style={{ gap: 24, flexWrap: "wrap", fontSize: 14 }}>
            <div><strong>Terms:</strong> 7/1/2026 or Net 10</div>
            <div><strong>Remit to:</strong> Arett Sales Corp. Attn: Accounts Payable, PO Box 536608, Pittsburgh, PA 15253-5908</div>
          </div>
          <div className="section-divider" />
          <AlertBanner type="info">
            <strong>SHOW DATES:</strong> Virtual Show: Saturday, 08/01/2026 - Thursday, 12/01/2026. Live Show: Wednesday, 09/16/2026 - Thursday, 09/17/2026
            <br />
            <span style={{ color: "var(--danger)", fontWeight: 600, marginTop: 4, display: "block" }}>
              Manufacturers/Exhibitors participating in the Open House must participate in the Virtual Show AND take a minimum of one 10 x 10 booth at the Live Show.
            </span>
          </AlertBanner>
        </FormCard>

        <FormCard title="Booth Space" subtitle="All live shows include virtual platform." icon={<Icon name="building" size={14} />}>
          <table className="contract-table">
            <thead>
              <tr>
                <th>Booth Description</th>
                <th>Unit Cost</th>
                <th>Total Units</th>
                <th>Total Due</th>
                <th>Internal Code 9002</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <label className="custom-checkbox">
                    <input type="checkbox" checked={formData.firstBoothChecked}
                      onChange={(e) => updateField("firstBoothChecked", e.target.checked)} />
                    <span>VIRTUAL And LIVE Show First 10' x 10' booth</span>
                  </label>
                </td>
                <td>$7,975.00</td>
                <td><input className="form-input" value="1" style={{ width: 70 }} readOnly /></td>
                <td><strong>{formData.firstBoothChecked ? "$7,975.00" : "$0.00"}</strong></td>
                <td>7316-0001</td>
              </tr>
              <tr>
                <td>
                  <label className="custom-checkbox">
                    <input type="checkbox" checked={formData.subsequentBoothChecked}
                      onChange={(e) => updateField("subsequentBoothChecked", e.target.checked)} />
                    <span>LIVE Show Subsequent 10' x 10'</span>
                  </label>
                </td>
                <td>$7,500.00</td>
                <td>
                  <input className={`form-input${errors.liveSubsequent ? " input-error" : ""}`} type="number" value={formData.liveSubsequent}
                    min={1}
                    onChange={(e) => updateField("liveSubsequent", Math.max(1, parseInt(e.target.value) || 1))}
                    style={{ width: 70 }} />
                  {errors.liveSubsequent && <span className="field-error" style={{ display: "block", marginTop: 4, fontSize: 11 }}>{errors.liveSubsequent}</span>}
                </td>
                <td><strong>{formData.subsequentBoothChecked ? `$${(7500 * formData.liveSubsequent).toLocaleString()}.00` : "$0.00"}</strong></td>
                <td>7316-0001</td>
              </tr>
              <tr>
                <td>
                  <label className="custom-checkbox">
                    <input type="checkbox" checked={formData.customBoothChecked}
                      onChange={(e) => updateField("customBoothChecked", e.target.checked)} />
                    <span>Other Booth Configuration:</span>
                  </label>
                  <input className={`form-input${errors.customSize || customSizeFormatError ? " input-error" : ""}`} value={formData.customSize}
                    onChange={(e) => { updateField("customSize", e.target.value); if (customSizeFormatError) setCustomSizeFormatError(""); }}
                    onBlur={(e) => {
                      const val = e.target.value.trim();
                      if (val && !/^\d+[xX]\d+$/.test(val)) {
                        setCustomSizeFormatError("Format must be length x width (e.g., 10x10)");
                      } else {
                        setCustomSizeFormatError("");
                      }
                    }}
                    style={{ width: 140, marginTop: 6 }} placeholder="e.g., 10x10" />
                  {(errors.customSize || customSizeFormatError) && <span className="field-error" style={{ display: "block", marginTop: 4, fontSize: 11 }}>{customSizeFormatError || errors.customSize}</span>}
                </td>
                <td>
                  <input className={`form-input${errors.customUnitCost ? " input-error" : ""}`} value={formData.customUnitCost}
                    onChange={(e) => updateField("customUnitCost", e.target.value)}
                    placeholder="$" style={{ width: 90 }} />
                  {errors.customUnitCost && <span className="field-error" style={{ display: "block", marginTop: 4, fontSize: 11 }}>{errors.customUnitCost}</span>}
                </td>
                <td>
                  <input className={`form-input${errors.customUnits ? " input-error" : ""}`} type="number" value={formData.customUnits}
                    min={1}
                    onChange={(e) => updateField("customUnits", Math.max(1, parseInt(e.target.value) || 1))}
                    style={{ width: 70 }} />
                  {errors.customUnits && <span className="field-error" style={{ display: "block", marginTop: 4, fontSize: 11 }}>{errors.customUnits}</span>}
                </td>
                <td>
                  <input className="form-input"
                    value={formData.customUnitCost && formData.customUnits
                      ? `$${(parseFloat(formData.customUnitCost) * parseFloat(formData.customUnits)).toLocaleString()}`
                      : ""}
                    placeholder="$" style={{ width: 90 }} readOnly />
                </td>
                <td>7316-0001</td>
              </tr>
              <tr className="total-row">
                <td colSpan={3}><strong>TOTAL BOOTH FEE:</strong></td>
                <td colSpan={2}><strong>${calculateBoothTotal().toLocaleString()}.00</strong></td>
              </tr>
            </tbody>
          </table>
          {errors.boothSelection && <span className="field-error" style={{ display: "block", marginTop: 8 }}>{errors.boothSelection}</span>}
          <p className="text-small mt-12">$75 wifi charge will be added to all invoices</p>
          <div className="section-divider" />
          <FormGroup label="Booth Configuration (cannot guarantee – indicate your preferences)" error={errors.boothConfig}>
            <div className="radio-group">
              {["Inline", "Endcap", "Island"].map((opt) => (
                <label className="custom-radio" key={opt}>
                  <input type="radio" name="boothConfig" value={opt}
                    checked={formData.boothConfig === opt}
                    onChange={(e) => updateField("boothConfig", e.target.value)} />
                  <span>{opt}</span>
                </label>
              ))}
            </div>
          </FormGroup>
          <FormGroup label="Comments/Special Notes/Put with Exhibitor(s):" style={{ marginTop: 12 }}>
            <textarea className="form-textarea" rows={4} value={formData.comments}
              onChange={(e) => updateField("comments", e.target.value)} />
          </FormGroup>
          <div className="section-divider" />
          <AlertBanner type="info">
            In the Event Arett is unable to host a live, in-person Open House, we will proceed exclusively with our enhanced Virtual Open House and you will be charged a fee of $5,000
          </AlertBanner>
          <div style={{ marginTop: 16 }}><strong style={{ fontSize: 14 }}>All Booths Will Be Provided:</strong></div>
          <ul style={{ fontSize: 13, paddingLeft: 20, color: "var(--neutral-600)", lineHeight: 2 }}>
            {["8 ft. H drape", "3 ft. H side drape", "Booth carpet", "ID Sign"].map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <AlertBanner type="info" style={{ marginTop: 12 }}>
            Note: Chairs and tables must be ordered separately from <a className="link" href="https://www.generalexposition.com/online-servicekit.php" target="_blank" rel="noopener noreferrer">General Exposition Services (enter ARETT in "Exhibitor Code" field)</a>.
                  All carpet must be "broom clean" or you will be charged for booth cleaning. Electric, water, booth cleaning must be ordered through the Convention Center. AV must be ordered through Convention Center. Order services using the online portals links provided earlier in this section.
          </AlertBanner>
        </FormCard>

        <FormCard title="Live Show Pegboard Rental" icon={<Icon name="tag" size={14} />}>
          <table className="contract-table">
            <thead><tr><th>Description</th><th>Unit Cost</th><th>Qty</th><th>Total Cost</th><th>Internal Code 9002</th></tr></thead>
            <tbody>
              <tr>
                <td><label className="custom-checkbox"><input type="checkbox" checked={formData.pegboardChecked} onChange={(e) => updateField("pegboardChecked", e.target.checked)} /><span>4' x 7' pegboard with 3 shelves</span></label></td>
                <td>$200</td>
                <td>
                  <input className="form-input" type="number" min={0} placeholder="#" value={formData.pegboardQty}
                    onChange={(e) => { const qty = Math.max(0, parseInt(e.target.value) || 0); updateField("pegboardQty", qty); updateField("pegboardTotal", (200 * qty).toString()); }}
                    style={{ width: 70 }} />
                </td>
                <td><input className="form-input" placeholder="$" value={formData.pegboardTotal} readOnly style={{ width: 90 }} /></td>
                <td>7316-0006</td>
              </tr>
            </tbody>
          </table>
        </FormCard>

        <FormCard title="Live Show Set-Up Date(s)" icon={<Icon name="calendar" size={14} />}>
          <div className="checkbox-group vertical">
            <label className="custom-checkbox">
              <input type="checkbox" checked={formData.setupMonday}
                onChange={(e) => updateField("setupMonday", e.target.checked)} />
              <span>Monday, 09/14/2026 7:00 am – 6:00 pm</span>
            </label>
            <label className="custom-checkbox">
              <input type="checkbox" checked={formData.setupTuesday}
                onChange={(e) => updateField("setupTuesday", e.target.checked)} />
              <span>Tuesday, 09/15/2026 7:00 am – 5:30 pm</span>
            </label>
          </div>
          <AlertBanner type="info" style={{ marginTop: 12 }}>
            "Show Floor" closes at 5:30 pm on the final setup day. All vendors must check into the Convention Center by NOON of day 2 of setup, and be completely set up by 5:30 PM to avoid a $500 late fee.
          </AlertBanner>
        </FormCard>

        <FormCard title="Booth Sale at Close of Show" icon={<Icon name="tag" size={14} />}>
          <FormGroup label="Do You Intend to Sell Your Booth at the Close of the Show?" error={errors.sellBooth}>
            <div className="radio-group">
              <label className="custom-radio">
                <input type="radio" name="sellBooth" value="yes" checked={formData.sellBooth === "yes"}
                  onChange={(e) => updateField("sellBooth", e.target.value)} />
                <span>YES, please send me the information</span>
              </label>
              <label className="custom-radio">
                <input type="radio" name="sellBooth" value="no" checked={formData.sellBooth === "no"}
                  onChange={(e) => updateField("sellBooth", e.target.value)} />
                <span>No, we will arrange to ship our freight back</span>
              </label>
            </div>
          </FormGroup>
        </FormCard>

        <FormCard title="Freight" icon={<Icon name="tag" size={14} />}>
          <AlertBanner type="info">
            <strong>Freight:</strong> Reference on all freight – see General Exposition Services packet for details – complete their forms and fill in below. To save money on freight, please consolidate shipping of boxes, crates, and pallets so they arrive the same day to the advance warehouse.
          </AlertBanner>
          <AlertBanner type="info" style={{ marginTop: 12 }}>
            Advance Freight Can be shipped and stored up to 30 days prior to move-in and will be taken directly to the exhibitors' booth on the day of set-up and returned to the dock at the close of the show. Direct To Show-Site Can be shipped in on set-up days only. General Exposition Services will be responsible for unloading/reloading any freight brought to show-site. Freight will be taken to exhibitors' booth and returned to the dock at the close of the show.
          </AlertBanner>
          <table className="contract-table" style={{ marginTop: 16 }}>
            <thead><tr><th>Type</th><th>Unit Cost</th><th>Quantity</th><th>Total</th><th>Internal Code</th></tr></thead>
            <tbody>
              <tr>
                <td><label className="custom-checkbox"><input type="checkbox" checked={formData.palletsChecked} onChange={(e) => updateField("palletsChecked", e.target.checked)} /><span>Pallets</span></label></td>
                <td>
                  <div className="input-prefix-wrap" style={{ width: 90 }}>
                    <span className="input-prefix">$</span>
                    <input className="form-input" value={formData.palletsUnitCost}
                      onChange={(e) => { const cost = e.target.value; updateField("palletsUnitCost", cost); if (formData.palletsQty) updateField("palletsTotal", (parseFloat(cost || 0) * parseFloat(formData.palletsQty)).toString()); }} />
                  </div>
                </td>
                <td>
                  <input className="form-input" type="number" min={0} placeholder="#" value={formData.palletsQty}
                    onChange={(e) => { const qty = Math.max(0, parseInt(e.target.value) || 0); updateField("palletsQty", qty); updateField("palletsTotal", (parseFloat(formData.palletsUnitCost || 0) * qty).toString()); }}
                    style={{ width: 70 }} />
                </td>
                <td><input className="form-input" placeholder="$" value={formData.palletsTotal} readOnly style={{ width: 90 }} /></td>
                <td>7316-0004</td>
              </tr>
              <tr>
                <td><label className="custom-checkbox"><input type="checkbox" checked={formData.cratesChecked} onChange={(e) => updateField("cratesChecked", e.target.checked)} /><span>Crates</span></label></td>
                <td>
                  <div className="input-prefix-wrap" style={{ width: 90 }}>
                    <span className="input-prefix">$</span>
                    <input className="form-input" value={formData.cratesUnitCost}
                      onChange={(e) => { const cost = e.target.value; updateField("cratesUnitCost", cost); if (formData.cratesQty) updateField("cratesTotal", (parseFloat(cost || 0) * parseFloat(formData.cratesQty)).toString()); }} />
                  </div>
                </td>
                <td>
                  <input className="form-input" type="number" min={0} placeholder="#" value={formData.cratesQty}
                    onChange={(e) => { const qty = Math.max(0, parseInt(e.target.value) || 0); updateField("cratesQty", qty); updateField("cratesTotal", (parseFloat(formData.cratesUnitCost || 0) * qty).toString()); }}
                    style={{ width: 70 }} />
                </td>
                <td><input className="form-input" placeholder="$" value={formData.cratesTotal} readOnly style={{ width: 90 }} /></td>
                <td>7316-0004</td>
              </tr>
            </tbody>
          </table>
        </FormCard>

        <FormCard title="Pre-Show Virtual Sales Meetings" icon={<Icon name="calendar" size={14} />}>
          <table className="contract-table">
            <thead>
              <tr><th>Option</th><th>Cost</th><th>Session Week 1</th><th>Preferred Date</th><th>Or</th><th>Session Week 2</th><th>Preferred Date</th></tr>
            </thead>
            <tbody>
              <tr>
                <td><label className="custom-checkbox"><input type="checkbox" checked={formData.virtualMeeting30min} onChange={(e) => updateField("virtualMeeting30min", e.target.checked)} /><span>30 minutes (discounted show rate)</span></label></td>
                <td>
                  <div className="input-prefix-wrap" style={{ width: 90 }}>
                    <span className="input-prefix">$</span>
                    <input className="form-input" value={formData.virtualMeeting30minUnitCost} onChange={(e) => updateField("virtualMeeting30minUnitCost", e.target.value)} />
                  </div>
                </td>
                <td>Tuesday, 07/21/2026 - Thursday, 07/23/2026</td>
                <td><DatePicker className="form-datepicker" value={formData.virtualMeeting30minWeek1 ? dayjs(formData.virtualMeeting30minWeek1) : null} onChange={(date) => updateField("virtualMeeting30minWeek1", date ? date.format("YYYY-MM-DD") : "")} format="MM/DD/YYYY" style={{ width: 150 }} disabledDate={(d) => !d || d.isBefore(dayjs("2026-07-21"), "day") || d.isAfter(dayjs("2026-07-23"), "day")} defaultPickerValue={dayjs("2026-07-21")} /></td>
                <td>or</td>
                <td>Tuesday, 08/18/2026 - Wednesday, 08/19/2026</td>
                <td><DatePicker className="form-datepicker" value={formData.virtualMeeting30minWeek2 ? dayjs(formData.virtualMeeting30minWeek2) : null} onChange={(date) => updateField("virtualMeeting30minWeek2", date ? date.format("YYYY-MM-DD") : "")} format="MM/DD/YYYY" style={{ width: 150 }} disabledDate={(d) => !d || d.isBefore(dayjs("2026-08-18"), "day") || d.isAfter(dayjs("2026-08-19"), "day")} defaultPickerValue={dayjs("2026-08-18")} /></td>
              </tr>
              <tr>
                <td><label className="custom-checkbox"><input type="checkbox" checked={formData.virtualMeeting20min} onChange={(e) => updateField("virtualMeeting20min", e.target.checked)} /><span>20 minutes (discounted show rate)</span></label></td>
                <td>
                  <div className="input-prefix-wrap" style={{ width: 90 }}>
                    <span className="input-prefix">$</span>
                    <input className="form-input" value={formData.virtualMeeting20minUnitCost} onChange={(e) => updateField("virtualMeeting20minUnitCost", e.target.value)} />
                  </div>
                </td>
                <td>Tuesday, 07/21/2026 - Thursday, 07/23/2026</td>
                <td><DatePicker className="form-datepicker" value={formData.virtualMeeting20minWeek1 ? dayjs(formData.virtualMeeting20minWeek1) : null} onChange={(date) => updateField("virtualMeeting20minWeek1", date ? date.format("YYYY-MM-DD") : "")} format="MM/DD/YYYY" style={{ width: 150 }} disabledDate={(d) => !d || d.isBefore(dayjs("2026-07-21"), "day") || d.isAfter(dayjs("2026-07-23"), "day")} defaultPickerValue={dayjs("2026-07-21")} /></td>
                <td>or</td>
                <td>Tuesday, 08/18/2026 - Wednesday, 08/19/2026</td>
                <td><DatePicker className="form-datepicker" value={formData.virtualMeeting20minWeek2 ? dayjs(formData.virtualMeeting20minWeek2) : null} onChange={(date) => updateField("virtualMeeting20minWeek2", date ? date.format("YYYY-MM-DD") : "")} format="MM/DD/YYYY" style={{ width: 150 }} disabledDate={(d) => !d || d.isBefore(dayjs("2026-08-18"), "day") || d.isAfter(dayjs("2026-08-19"), "day")} defaultPickerValue={dayjs("2026-08-18")} /></td>
              </tr>
            </tbody>
          </table>
          <FormGroup label="Notes to Merchant:" style={{ marginTop: 16 }}>
            <textarea className="form-textarea" rows={4} value={formData.merchantNotes}
              onChange={(e) => updateField("merchantNotes", e.target.value)} />
          </FormGroup>
        </FormCard>

        <FormCard title="New Products & Packaging Exhibit" icon={<Icon name="star" size={14} />}>
          <div className="radio-group" style={{ marginBottom: errors.newProductsParticipate ? 4 : 12 }}>
            <label className="custom-radio">
              <input type="radio" name="newProductsParticipate" value="yes"
                checked={formData.newProductsParticipate === "yes"}
                onChange={() => { updateField("newProductsParticipate", "yes"); updateField("newProductsChecked", true); updateField("newProductsCost", parseFloat(formData.newProductsUnitCost) || 0); }} />
              <span>Yes, I opt to participate</span>
            </label>
            <label className="custom-radio">
              <input type="radio" name="newProductsParticipate" value="no"
                checked={formData.newProductsParticipate === "no"}
                onChange={() => { updateField("newProductsParticipate", "no"); updateField("newProductsChecked", false); updateField("newProductsCost", 0); }} />
              <span>No, I opt NOT to participate</span>
            </label>
          </div>
          {errors.newProductsParticipate && <span className="field-error" style={{ display: "block", marginBottom: 12 }}>{errors.newProductsParticipate}</span>}
          {formData.newProductsParticipate === "yes" && (
            <table className="contract-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Unit Cost</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>For up to 5 items. See tab 6 to provide new product selection.</td>
                  <td>
                    <div className="input-prefix-wrap" style={{ width: 90 }}>
                      <span className="input-prefix">$</span>
                      <input className="form-input" value={formData.newProductsUnitCost}
                        onChange={(e) => { updateField("newProductsUnitCost", e.target.value); updateField("newProductsCost", parseFloat(e.target.value) || 0); }} />
                    </div>
                  </td>
                  <td><strong>${parseFloat(formData.newProductsUnitCost || 0).toFixed(2)}</strong></td>
                </tr>
              </tbody>
            </table>
          )}
          <p className="text-small mt-8">All vendor participants MUST deliver their New Product to the New Products Exhibit no later than Tuesday, 09/15/2026 at noon (12pm).</p>
        </FormCard>

        <FormCard title="Power Alley" icon={<Icon name="star" size={14} />}>
          <div className="radio-group" style={{ marginBottom: errors.powerAlleyParticipate ? 4 : 12 }}>
            <label className="custom-radio">
              <input type="radio" name="powerAlleyParticipate" value="yes"
                checked={formData.powerAlleyParticipate === "yes"}
                onChange={() => { updateField("powerAlleyParticipate", "yes"); updateField("powerAlleyChecked", true); }} />
              <span>Yes, I opt to participate</span>
            </label>
            <label className="custom-radio">
              <input type="radio" name="powerAlleyParticipate" value="no"
                checked={formData.powerAlleyParticipate === "no"}
                onChange={() => { updateField("powerAlleyParticipate", "no"); updateField("powerAlleyChecked", false); updateField("powerAlleyQty", ""); updateField("powerAlleyTotal", ""); }} />
              <span>No, I opt NOT to participate</span>
            </label>
          </div>
          {errors.powerAlleyParticipate && <span className="field-error" style={{ display: "block", marginBottom: 12 }}>{errors.powerAlleyParticipate}</span>}
          {formData.powerAlleyParticipate === "yes" && (
            <table className="contract-table">
              <thead><tr><th>Description</th><th>Cost per item</th><th>Item Qty</th><th>Total Cost</th><th>Internal Code 9002</th></tr></thead>
              <tbody>
                <tr>
                  <td>Per item</td>
                  <td>
                    <div className="input-prefix-wrap" style={{ width: 90 }}>
                      <span className="input-prefix">$</span>
                      <input className="form-input" value={formData.powerAlleyUnitCost}
                        onChange={(e) => { const cost = e.target.value; updateField("powerAlleyUnitCost", cost); if (formData.powerAlleyQty) updateField("powerAlleyTotal", (parseFloat(cost || 0) * parseFloat(formData.powerAlleyQty)).toString()); }} />
                    </div>
                  </td>
                  <td>
                    <input className={`form-input${errors.powerAlleyQty ? " input-error" : ""}`} type="number" min={0} placeholder="#" value={formData.powerAlleyQty}
                      onChange={(e) => { const qty = Math.max(0, parseInt(e.target.value) || 0); updateField("powerAlleyQty", qty); updateField("powerAlleyTotal", (parseFloat(formData.powerAlleyUnitCost || 0) * qty).toString()); }}
                      style={{ width: 70 }} />
                    {errors.powerAlleyQty && <span className="field-error" style={{ display: "block", marginTop: 4, fontSize: 11 }}>{errors.powerAlleyQty}</span>}
                  </td>
                  <td><input className="form-input" placeholder="$" value={formData.powerAlleyTotal} readOnly style={{ width: 90 }} /></td>
                  <td>7316-0001</td>
                </tr>
              </tbody>
            </table>
          )}
        </FormCard>

        <FormCard title="Special Events & Programs — Doorbusters" icon={<Icon name="star" size={14} />}>
          <p className="text-small mb-12">Doorbusters – min discount of 20%</p>
          <div className="radio-group" style={{ marginBottom: errors.doorbustersParticipate ? 4 : 12 }}>
            <label className="custom-radio">
              <input type="radio" name="doorbustersParticipate" value="yes"
                checked={formData.doorbustersParticipate === "yes"}
                onChange={() => { updateField("doorbustersParticipate", "yes"); updateField("doorbustersChecked", true); }} />
              <span>Yes, I opt to participate</span>
            </label>
            <label className="custom-radio">
              <input type="radio" name="doorbustersParticipate" value="no"
                checked={formData.doorbustersParticipate === "no"}
                onChange={() => { updateField("doorbustersParticipate", "no"); updateField("doorbustersChecked", false); updateField("doorbustersQty", ""); updateField("doorbustersTotal", ""); }} />
              <span>No, I opt NOT to participate</span>
            </label>
          </div>
          {errors.doorbustersParticipate && <span className="field-error" style={{ display: "block", marginBottom: 12 }}>{errors.doorbustersParticipate}</span>}
          {formData.doorbustersParticipate === "yes" && (
            <table className="contract-table">
              <thead><tr><th>Description</th><th>Unit Cost</th><th>Qty</th><th>Total Cost</th><th>Internal Code 9002</th></tr></thead>
              <tbody>
                <tr>
                  <td>Per deal</td>
                  <td>
                    <div className="input-prefix-wrap" style={{ width: 90 }}>
                      <span className="input-prefix">$</span>
                      <input className="form-input" value={formData.doorbustersUnitCost}
                        onChange={(e) => { const cost = e.target.value; updateField("doorbustersUnitCost", cost); if (formData.doorbustersQty) updateField("doorbustersTotal", (parseFloat(cost || 0) * parseFloat(formData.doorbustersQty)).toString()); }} />
                    </div>
                  </td>
                  <td>
                    <input className={`form-input${errors.doorbustersQty ? " input-error" : ""}`} type="number" min={0} placeholder="#" value={formData.doorbustersQty}
                      onChange={(e) => { const qty = Math.max(0, parseInt(e.target.value) || 0); updateField("doorbustersQty", qty); updateField("doorbustersTotal", (parseFloat(formData.doorbustersUnitCost || 0) * qty).toString()); }}
                      style={{ width: 70 }} />
                    {errors.doorbustersQty && <span className="field-error" style={{ display: "block", marginTop: 4, fontSize: 11 }}>{errors.doorbustersQty}</span>}
                  </td>
                  <td><input className="form-input" placeholder="$" value={formData.doorbustersTotal} readOnly style={{ width: 90 }} /></td>
                  <td>7316-0001</td>
                </tr>
              </tbody>
            </table>
          )}
          <div className="price-summary" style={{ marginTop: 16 }}>
            <div className="flex-row" style={{ justifyContent: "space-between" }}>
              <div>
                <div className="price-summary-label">Contract Total Due</div>
                <div className="price-summary-amount">${calculateTotal().toLocaleString()}.00</div>
              </div>
            </div>
          </div>
        </FormCard>

        <FormCard title="Arett Open House Policies and Procedures">
          <div className="policy-container">
            <p>Arett is committed to providing a safe and welcoming environment at its trade shows and events. In order to provide this, all attendees, media, speakers, vendors, exhibitors, volunteers, organizers, venue staff, and guests are expected to assist in this effort by following our Policies and Procedures at each event, including ancillary events such as official or unofficial social gatherings. Violations of these Policies and Procedures can be expected to result in consequences proportionate to the violation, ranging from a request to comply to removal from the event or premises.</p>
            {[
              { title: "Business Conveniences", text: "If an Arett show offers a business center providing services for guests, such as computer access, charging stations, fax center, mailing services, those services are provided strictly as a convenience to attendees. An outside vendor may be engaged to offer such services, but in no event shall Arett be liable for any failure to offer services or shall these conveniences be considered to be included in the cost of attendance or an obligation of Arett to provide." },
              { title: "Age Requirements", text: "No one under the age of 18 is permitted to register and attend Arett trade shows or events unless accompanied by a parent or guardian throughout the show or event." },
              { title: "Alcohol", text: "Whether alcohol is available at official or unofficial events associated with Arett trade shows, participants (vendors, employees, customers, etc.) are expected to drink responsibly and remain professional and respectful at all times." },
              { title: "Indemnification and Insurance:", text: "Arett Sales will employ reasonable security measures and take reasonable precautions to safeguard the Exhibitor's equipment and materials during the show period. Arett Sales assumes no liability for losses or damage. Exhibitors must carry general liability and/or excess liability insurance with minimum policy limits of Two Million Dollars ($2,000,000) each occurrence." },
            ].map((item, i) => (
              <div key={i}>
                <div className="policy-section-h">{item.title}</div>
                <p>{item.text}</p>
              </div>
            ))}
          </div>
          <div className="section-divider" />
          <SignatureSection
            name={formData.signatureName}
            date={formData.signatureDate}
            acknowledge={formData.signatureAcknowledge}
            onNameChange={(name) => updateField("signatureName", name)}
            onDateChange={(date) => updateField("signatureDate", date)}
            onAcknowledge={(v) => updateField("signatureAcknowledge", v)}
          />
          {(errors.signatureName || errors.signatureDate) && (
            <div className="step-error-summary" style={{ marginTop: 8 }}>
              {errors.signatureName && <span className="field-error">{errors.signatureName}</span>}
              {errors.signatureDate && <span className="field-error">{errors.signatureDate}</span>}
            </div>
          )}
          <div className="section-divider" />
          <FormCard title="Accounting Contact">
            <p className="text-small mb-12">Provide contact information below for who should receive invoice. Required fields are Accounting Contact Name, Billing City, State, Zip and Email address.</p>
            <FormGrid cols={2}>
              <FormGroup label="Accounting Contact" error={errors.accountingContact}>
                <input className={`form-input ${errors.accountingContact ? "input-error" : ""}`} value={formData.accountingContact}
                  onChange={(e) => updateField("accountingContact", e.target.value)} />
              </FormGroup>
              <FormGroup label="Billing Address">
                <input className="form-input" value={formData.billingAddress}
                  onChange={(e) => updateField("billingAddress", e.target.value)} />
              </FormGroup>
              <FormGroup label="Billing City, State, Zip" error={errors.billingCityStateZip}>
                <input className={`form-input ${errors.billingCityStateZip ? "input-error" : ""}`} value={formData.billingCityStateZip}
                  onChange={(e) => updateField("billingCityStateZip", e.target.value)} />
              </FormGroup>
              <FormGroup label="Billing Phone Number">
                <div className="form-phone-input">
                  <PhoneInput
                    country="us"
                    value={formData.billingPhone}
                    onChange={(val) => updateField("billingPhone", val)}
                    enableSearch
                    disableSearchIcon
                    inputProps={{ name: "billingPhone" }}
                  />
                </div>
              </FormGroup>
              <FormGroup label="Email Address" error={errors.email || emailFormatError}>
                <input
                  className={`form-input ${errors.email || emailFormatError ? "input-error" : ""}`}
                  value={formData.email}
                  onChange={(e) => {
                    updateField("email", e.target.value);
                    if (emailFormatError) setEmailFormatError("");
                  }}
                  onBlur={(e) => {
                    const val = e.target.value.trim();
                    if (val && !EMAIL_REGEX.test(val)) {
                      setEmailFormatError("Please enter a valid email address");
                    } else {
                      setEmailFormatError("");
                    }
                  }}
                />
              </FormGroup>
            </FormGrid>
          </FormCard>
          <PaymentSection
            title="Payment Information (July 1st of current year or Net10 days after signed agreement payment must be made via check or credit card)"
            paymentMethod={formData.paymentMethod}
            invoiceAddressText={formData.invoiceAddressText}
            onPaymentMethodChange={(val) => updateField("paymentMethod", val)}
            onAddressChange={(val) => updateField("invoiceAddressText", val)}
          />
          {errors.paymentMethod && (
            <span className="field-error" style={{ marginTop: 8, display: "block" }}>{errors.paymentMethod}</span>
          )}
        </FormCard>
      </div>
    </fieldset>
  );
};

export default Step3;
